import { AppDispatch } from 'store';
import { LoginFormValues, LoginResponse } from './types/index';
import { handleMaintenanceCheck } from 'utils/networkConnectivity';
import { syncLanguageFromBackend } from 'features/language/languageManager';

/**
 * Apply the culture the backend reports for the user who just authenticated.
 *
 * Delegates to the language manager, which guards each store separately. The
 * previous implementation gated every write — i18next included — on a single
 * comparison against localStorage, so when localStorage already matched but
 * i18next did not, login could not repair the mismatch.
 */
export async function syncLanguage(
  dispatch: AppDispatch,
  language: string,
): Promise<void> {
  await syncLanguageFromBackend(dispatch, language);
}

export async function handleLoginSuccess(
  payload: LoginResponse,
  dispatch: AppDispatch,
  router: { push: (path: string) => void },
  returnUrl: string,
): Promise<void> {
  // Language
  if (payload.culture) {
    await syncLanguage(dispatch, payload.culture);
  }
  handleMaintenanceCheck();

  router.push(returnUrl);
}

export function handleLoginFailure(
  resultAction: {
    payload?: ApiResponse<LoginResponse>;
  },
  values: LoginFormValues,
  router: { push: (path: string) => void },
  setErrors: (errors: Record<string, string>) => void,
  setStatus: (status?: string) => void,
): void {
  if (resultAction.payload?.data?.isTwoFactorEnabled) {
    router.push(
      `/account/validatecode?email=${encodeURIComponent(values.email)}`,
    );
    return;
  }

  const errorMessage = resultAction.payload?.message ?? 'An error occurred';
  const validationErrors: ValidationError[] =
    resultAction.payload?.validationErrors ?? [];

  const formErrors: Record<string, string> = {};
  validationErrors.forEach((err) => {
    formErrors[err.key] = err.message;
  });

  setErrors(formErrors);
  setStatus(errorMessage);
}
